import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * Opens the shared quick-add dialog pre-filled with a product's full details,
 * reusing the same dialog markup, styles and morph target that Quick Add's
 * "Choose options" flow already populates - Quick View is just an additional
 * trigger into that same modal, not a second modal implementation.
 *
 * Module-level state is intentional: only one quick-add-dialog exists on the
 * page, so "which fetch is current" and "which trigger opened it" are
 * properties of that shared dialog, not of any single product card.
 *
 * @extends Component
 */
export class QuickViewComponent extends Component {
  /** @type {AbortController | null} */
  static #abortController = null;
  /** @type {number} */
  static #requestToken = 0;

  get #productUrl() {
    return this.dataset.productUrl || '';
  }

  get #trigger() {
    return this.querySelector('.quick-view__trigger');
  }

  /**
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const productUrl = this.#productUrl;
    if (!productUrl) return;

    QuickViewComponent.#abortController?.abort();
    const abortController = new AbortController();
    QuickViewComponent.#abortController = abortController;
    const token = ++QuickViewComponent.#requestToken;

    let gridElement = null;

    try {
      const response = await fetch(productUrl, { signal: abortController.signal });
      if (!response.ok) return;

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      gridElement = html.querySelector('[data-product-grid-content]');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      throw error;
    }

    // A later click (this product or another one) superseded this request while it was
    // in flight - drop the stale response instead of morphing it into the modal.
    if (token !== QuickViewComponent.#requestToken) return;
    if (!gridElement) return;

    this.#populateModal(gridElement, productUrl);
    this.#openModal();
  };

  /**
   * @param {Element} gridElement
   * @param {string} productUrl
   */
  #populateModal(gridElement, productUrl) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const content = /** @type {Element} */ (gridElement.cloneNode(true));

    if (isMobileBreakpoint()) {
      const productDetails = content.querySelector('.product-details');
      const productFormComponent = content.querySelector('product-form-component');
      const variantPicker = content.querySelector('variant-picker');
      const productPrice = content.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = productUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);
      if (productPrice) productHeader.appendChild(productPrice);
      content.appendChild(productHeader);

      if (variantPicker) content.appendChild(variantPicker);
      if (productFormComponent) content.appendChild(productFormComponent);

      productDetails?.remove();
    }

    const payload = content.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, content);
  }

  #openModal() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof DialogComponent)) return;

    const trigger = this.#trigger;
    if (trigger instanceof HTMLElement) {
      dialogComponent.addEventListener(
        DialogCloseEvent.eventName,
        () => {
          if (trigger.isConnected) trigger.focus();
        },
        { once: true }
      );
    }

    dialogComponent.showDialog();
  }
}

if (!customElements.get('quick-view-component')) {
  customElements.define('quick-view-component', QuickViewComponent);
}
